import React from 'react'
import { noop } from 'lodash-es'
import { createLinearScale, clamp } from '@utils/math'
import Surface from './Surface'
import Viewport from './Viewport'
import Handle from './Handle'
import { DragEventData } from '../../types/gesture-types'
import { Position, Cursor, HandleCursor, HandleIdentifier } from '../../types/types'

type CursorsObject = {
  surface: Cursor
  viewport: Cursor
  leftHandle: HandleCursor
  rightHandle: HandleCursor
}

type ScrubberProps = {
  x: number
  y: number
  width: number
  height: number
  surfaceWidth: number
  surfaceHeight: number
  viewportWidth: number
  viewportHeight: number
  viewportX: number
  viewportY: number
  handleWidth: number
  handleHeight: number
  maxWidthRatio: number
  minWidthRatio: number
  cursors: CursorsObject
  onViewportPanStart: () => void
  onViewportPanMove: (nextLeft: number, nextRight: number) => void
  onViewportPanEnd: () => void
}

const Scrubber: React.FC<ScrubberProps> = ({
  x,
  y,
  width,
  height,
  onViewportPanStart = noop,
  onViewportPanMove = noop,
  onViewportPanEnd = noop,
  surfaceWidth,
  surfaceHeight,
  viewportWidth,
  viewportHeight,
  viewportX,
  viewportY,
  maxWidthRatio,
  minWidthRatio,
  handleWidth,
  handleHeight,
  cursors,
}) => {
  const prevPositionRef = React.useRef<Position | null>(null)

  const handleViewportPanStart = (dragEventData: DragEventData) => {
    // If there's a native event, prevent default if possible
    if (dragEventData.event && dragEventData.event.preventDefault) {
      dragEventData.event.preventDefault()
    }

    const center = {
      x: dragEventData.event?.clientX || 0,
      y: dragEventData.event?.clientY || 0,
    }
    prevPositionRef.current = center

    onViewportPanStart()
  }

  const handleViewportPanMove = (dragEventData: DragEventData) => {
    // If there's a native event, prevent default if possible
    if (dragEventData.event && dragEventData.event.preventDefault) {
      dragEventData.event.preventDefault()
    }

    const center = {
      x: dragEventData.event?.clientX || 0,
      y: dragEventData.event?.clientY || 0,
    }

    const prevCenter = prevPositionRef.current
    prevPositionRef.current = center
    if (prevCenter) {
      // Note: Assigning to variables as one of the objects is sometimes null and we would like to know which one (in production)
      const prevCenterX = prevCenter.x
      const currentCenterX = center.x

      const movementX = (prevCenterX - currentCenterX) * -1
      const movementY = 0

      moveViewport(movementX, movementY)
    }
  }

  const handleViewportPanEnd = (dragEventData: DragEventData) => {
    // If there's a native event, prevent default if possible
    if (dragEventData.event && dragEventData.event.preventDefault) {
      dragEventData.event.preventDefault()
    }

    prevPositionRef.current = null
    onViewportPanEnd()
  }

  const moveViewport = (movementX: number, _movementY: number) => {
    const minX = 0
    const maxX = surfaceWidth - viewportWidth
    const nextViewportX = clamp(viewportX + movementX, minX, maxX)

    const newViewportLeft = Math.abs(nextViewportX)
    const newViewportRight = newViewportLeft + viewportWidth

    const scale = createLinearScale([0, surfaceWidth], [0, 1])
    const nextLeft = scale(newViewportLeft)
    const nextRight = scale(newViewportRight)

    onViewportPanMove(nextLeft, nextRight)
  }

  const handleHandlePanStart = (dragEventData: DragEventData, _handleIdentifier: HandleIdentifier) => {
    const center = {
      x: dragEventData.event?.clientX || 0,
      y: dragEventData.event?.clientY || 0,
    }
    prevPositionRef.current = center
    onViewportPanStart()
  }

  const handleHandlePanMove = (dragEventData: DragEventData, handleIdentifier: HandleIdentifier) => {
    // If there's a native event, prevent default if possible
    if (dragEventData.event && dragEventData.event.preventDefault) {
      dragEventData.event.preventDefault()
    }

    const center = {
      x: dragEventData.event?.clientX || 0,
      y: dragEventData.event?.clientY || 0,
    }
    const prevCenter = prevPositionRef.current
    prevPositionRef.current = center

    if (prevCenter) {
      // Note: Assigning to variables as one of the objects is sometimes null and we would like to know which one (in production)
      const prevCenterX = prevCenter.x
      const currentCenterX = center.x

      const movementX = (prevCenterX - currentCenterX) * -1
      const movementY = 0

      moveHandle(movementX, movementY, handleIdentifier)
    }
  }

  const handleHandlePanEnd = (dragEventData: DragEventData, _handleIdentifier: HandleIdentifier) => {
    // If there's a native event, prevent default if possible
    if (dragEventData.event && dragEventData.event.preventDefault) {
      dragEventData.event.preventDefault()
    }

    prevPositionRef.current = null
    onViewportPanEnd()
  }

  const moveHandle = (movementX: number, _movementY: number, handleIdentifier: HandleIdentifier) => {
    let minX, maxX, nextHandleX, newViewportLeft, newViewportRight, nextLeft, nextRight

    const minViewportWidth = surfaceWidth * maxWidthRatio
    const maxViewportWidth = surfaceWidth * minWidthRatio

    const scale = createLinearScale([0, surfaceWidth], [0, 1])

    // calculate the current left and right, then use that, combined with the min/max size and bounds to calcualte min left and right
    switch (handleIdentifier) {
      case 'left': {
        const currentRightX = viewportX + viewportWidth
        minX = clamp(currentRightX - maxViewportWidth, 0, surfaceWidth)
        maxX = clamp(currentRightX - minViewportWidth, 0, surfaceWidth)
        nextHandleX = clamp(viewportX + movementX, minX, maxX)

        newViewportRight = viewportX + viewportWidth
        newViewportLeft = Math.abs(nextHandleX)

        nextLeft = scale(newViewportLeft)
        nextRight = scale(newViewportRight)
        break
      }

      case 'right': {
        const currentLeftX = viewportX
        minX = clamp(currentLeftX + minViewportWidth, 0, surfaceWidth)
        maxX = clamp(currentLeftX + maxViewportWidth, 0, surfaceWidth)
        nextHandleX = clamp(viewportX + viewportWidth + movementX, minX, maxX)

        newViewportRight = Math.abs(nextHandleX)
        newViewportLeft = viewportX

        nextLeft = scale(newViewportLeft)
        nextRight = scale(newViewportRight)
        break
      }
    }
    onViewportPanMove(nextLeft, nextRight)
  }

  const scrubberStyle: React.CSSProperties = {
    left: x,
    top: y,
    width: width,
    height: height,
    zIndex: 1,
  }

  const middleY = viewportHeight / 2

  const leftHandleX = viewportX
  const rightHandleX = viewportX + viewportWidth
  return (
    <div className="scrubber" style={scrubberStyle}>
      <Surface x={0} y={0} width={surfaceWidth} height={surfaceHeight} cursor={cursors.surface} />
      <Viewport
        x={viewportX}
        y={viewportY}
        width={viewportWidth}
        height={viewportHeight}
        cursor={cursors.viewport}
        onPanStart={handleViewportPanStart}
        onPanMove={handleViewportPanMove}
        onPanEnd={handleViewportPanEnd}
      />
      <Handle
        x={leftHandleX}
        y={middleY}
        width={handleWidth}
        height={handleHeight}
        handle={'left'}
        cursor={cursors.leftHandle}
        onPanStart={handleHandlePanStart}
        onPanMove={handleHandlePanMove}
        onPanEnd={handleHandlePanEnd}
      />
      <Handle
        x={rightHandleX}
        y={middleY}
        width={handleWidth}
        height={handleHeight}
        handle={'right'}
        cursor={cursors.rightHandle}
        onPanStart={handleHandlePanStart}
        onPanMove={handleHandlePanMove}
        onPanEnd={handleHandlePanEnd}
      />
    </div>
  )
}

export default Scrubber
